import React, { useEffect, useState } from 'react';
import {
  CheckSquare,
  Save,
  Settings as SettingsIcon,
  ShieldCheck,
  User,
  Server,
  Wifi,
  Cloud,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Loader,
} from 'lucide-react';
import '../App.css';
import {
  AVAILABLE_OBJECT_CLASSES,
  DEFAULT_SELECTED_OBJECTS,
  getSelectedObjects,
  saveSelectedObjects,
} from '../utils/detectionSettings';
import {
  getApiBaseUrl,
  getStoredBackendUrl,
  setStoredBackendUrl,
  RENDER_BACKEND_URL,
} from '../utils/api';

export default function Settings() {
  // ── Detection objects ────────────────────────────────────────────────────
  const [selectedObjects, setSelectedObjects] = useState(DEFAULT_SELECTED_OBJECTS);
  const [savedMessage, setSavedMessage] = useState('');
  const [error, setError] = useState('');

  // ── Backend URL ──────────────────────────────────────────────────────────
  const [backendInput, setBackendInput] = useState('');
  const [activeBackend, setActiveBackend] = useState('');
  const [testStatus, setTestStatus] = useState(null); // null | 'testing' | 'ok' | 'fail'
  const [testMsg, setTestMsg] = useState('');

  useEffect(() => {
    setSelectedObjects(getSelectedObjects());
    const stored = getStoredBackendUrl();
    setBackendInput(stored || '');
    setActiveBackend(getApiBaseUrl());
  }, []);

  // ── Object class handlers ────────────────────────────────────────────────
  const toggleObject = (cls) => {
    setSavedMessage('');
    setError('');
    setSelectedObjects(cur =>
      cur.includes(cls) ? cur.filter(c => c !== cls) : [...cur, cls]
    );
  };

  const handleSave = () => {
    if (selectedObjects.length === 0) {
      setError('Select at least one object to keep detection enabled.');
      return;
    }
    saveSelectedObjects(selectedObjects);
    setSavedMessage('Detection settings saved.');
  };

  // ── Backend URL handlers ─────────────────────────────────────────────────
  const applyBackendUrl = (url) => {
    setStoredBackendUrl(url);
    setBackendInput(url);
    setActiveBackend(getApiBaseUrl());
    setTestStatus(null);
    setTestMsg('');
    setSavedMessage(`Backend URL saved. Reload the page for all requests to use ${url || 'auto-detect'}.`);
  };

  const handleTestBackend = async () => {
    setTestStatus('testing');
    setTestMsg('');
    const urlToTest = backendInput.trim() || RENDER_BACKEND_URL;
    const testEndpoint = urlToTest.replace(/\/api$/, '').replace(/\/$/, '') + '/api/analytics/health';
    try {
      const res = await fetch(testEndpoint, { signal: AbortSignal.timeout(10000) });
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        setTestStatus('ok');
        setTestMsg(`Connected ✓  status: ${json.status || 'ok'}`);
      } else {
        setTestStatus('fail');
        setTestMsg(`HTTP ${res.status} — ${json.error || 'unexpected response'}`);
      }
    } catch (e) {
      setTestStatus('fail');
      setTestMsg(`Cannot reach ${testEndpoint} — ${e.message}`);
    }
  };

  const isRenderActive = !getStoredBackendUrl() || getStoredBackendUrl().includes('onrender.com');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-white sm:text-3xl">Settings</h1>
          <p className="text-gray-400 mt-2">Configure backend connection and object detection classes.</p>
        </div>
        <div className="glass-panel p-4 hidden md:flex items-center gap-3">
          <SettingsIcon className="w-6 h-6 text-cyan-400" />
          <div>
            <p className="text-sm text-gray-400">Active classes</p>
            <p className="text-xl font-semibold text-white">{selectedObjects.length}</p>
          </div>
        </div>
      </div>

      {/* ── Backend URL panel ─────────────────────────────────────────────── */}
      <div className="glass-panel p-6 space-y-5">
        <div className="flex items-center gap-3">
          <Server className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-semibold text-white">Backend Connection</h2>
        </div>

        <p className="min-w-0 break-words text-sm text-gray-400">
          Currently active: <code className="text-cyan-300">{activeBackend}</code>
        </p>

        {/* Preset buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Render cloud */}
          <button
            onClick={() => applyBackendUrl('')}
            className={`flex items-start gap-3 p-4 rounded-xl border text-left transition-all ${
              isRenderActive
                ? 'border-cyan-400 bg-cyan-400/10'
                : 'border-slate-600 bg-slate-800/50 hover:border-slate-500'
            }`}
          >
            <Cloud className={`w-5 h-5 mt-0.5 shrink-0 ${isRenderActive ? 'text-cyan-300' : 'text-gray-400'}`} />
            <div>
              <p className={`font-medium text-sm ${isRenderActive ? 'text-cyan-200' : 'text-gray-300'}`}>
                Render Cloud {isRenderActive && '✓'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Image detection &amp; webcam. Cannot access local cameras.</p>
              <p className="mt-1 break-all font-mono text-xs text-gray-600">objectdetection-6gnn.onrender.com</p>
            </div>
          </button>

          {/* Local Docker */}
          <button
            onClick={() => applyBackendUrl('http://localhost:5000')}
            className={`flex items-start gap-3 p-4 rounded-xl border text-left transition-all ${
              !isRenderActive && backendInput.includes('localhost')
                ? 'border-green-400 bg-green-400/10'
                : 'border-slate-600 bg-slate-800/50 hover:border-slate-500'
            }`}
          >
            <Wifi className={`w-5 h-5 mt-0.5 shrink-0 ${
              !isRenderActive && backendInput.includes('localhost') ? 'text-green-300' : 'text-gray-400'
            }`} />
            <div>
              <p className={`font-medium text-sm ${
                !isRenderActive && backendInput.includes('localhost') ? 'text-green-200' : 'text-gray-300'
              }`}>
                Local Docker {!isRenderActive && backendInput.includes('localhost') && '✓'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Full camera access — RTSP &amp; WiFi IP cameras on your LAN.</p>
              <p className="mt-1 break-all font-mono text-xs text-gray-600">localhost:5000</p>
            </div>
          </button>
        </div>

        {/* Custom URL input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Custom backend URL <span className="text-gray-500 font-normal">(leave blank to use Render cloud)</span>
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={backendInput}
              onChange={e => setBackendInput(e.target.value)}
              placeholder="http://192.168.1.50:5000  or  http://localhost:5000"
              className="min-w-0 flex-1 rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 font-mono text-sm text-white placeholder-gray-500 focus:border-cyan-400 focus:outline-none"
            />
            <button
              onClick={handleTestBackend}
              disabled={testStatus === 'testing'}
              className="btn-secondary flex items-center justify-center gap-2 sm:shrink-0"
            >
              {testStatus === 'testing'
                ? <Loader className="w-4 h-4 animate-spin" />
                : <RefreshCw className="w-4 h-4" />}
              Test
            </button>
            <button
              onClick={() => applyBackendUrl(backendInput)}
              className="btn-primary flex items-center justify-center gap-2 sm:shrink-0"
            >
              <Save className="w-4 h-4" />
              Apply
            </button>
          </div>

          {testStatus === 'ok' && (
            <div className="flex items-center gap-2 text-sm text-green-300">
              <CheckCircle2 className="w-4 h-4" />
              {testMsg}
            </div>
          )}
          {testStatus === 'fail' && (
            <div className="flex items-start gap-2 text-sm text-red-300">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span className="min-w-0 break-words">{testMsg}</span>
            </div>
          )}
        </div>

        {/* Local Docker instructions */}
        <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4 space-y-3">
          <p className="text-sm font-semibold text-white flex items-center gap-2">
            <Wifi className="w-4 h-4 text-cyan-400" />
            How to start the local backend for RTSP / WiFi cameras
          </p>
          <ol className="text-sm text-gray-400 space-y-2 list-decimal list-inside">
            <li>Open a terminal in your project folder</li>
            <li>
              Run:{' '}
              <code className="bg-slate-800 text-cyan-300 px-2 py-0.5 rounded text-xs">
                docker compose up backend --build
              </code>
            </li>
            <li>Wait for <code className="text-cyan-300 text-xs">Booting worker with pid</code> in the logs (~2 min first time)</li>
            <li>Click <strong className="text-white">Local Docker ✓</strong> above (or type your machine IP if accessing from another device)</li>
            <li>Click <strong className="text-white">Apply</strong> then reload the page</li>
            <li>Go to <strong className="text-white">Stream Processor → RTSP Camera</strong> and enter your camera URL</li>
          </ol>
          <div className="rounded-lg bg-slate-800 p-3 font-mono text-xs text-gray-300">
            <p className="text-gray-500 mb-1"># From another device on the same network:</p>
            <p className="break-all">REACT_APP_API_URL = http://YOUR_MACHINE_IP:5000</p>
            <p className="text-gray-500 mt-1"># Find your machine IP with: ifconfig | grep "inet 192"</p>
          </div>
        </div>
      </div>

      {/* ── Detection object classes ─────────────────────────────────────── */}
      <div className="glass-panel p-6">
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-xl border border-cyan-500/20 bg-slate-900/70 p-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-cyan-400" />
              <p className="text-sm font-semibold text-white">Full YOLOv8 Class Catalog</p>
            </div>
            <p className="mt-2 text-sm text-gray-400">
              All standard COCO classes available and passed to the backend filter.
            </p>
          </div>
          <div className="rounded-xl border border-amber-500/20 bg-slate-900/70 p-4">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-amber-400" />
              <p className="text-sm font-semibold text-white">Retail Analytics Guardrail</p>
            </div>
            <p className="mt-2 text-sm text-gray-400">
              Queue and operator analytics require the <code>person</code> class.
            </p>
          </div>
          <div className="rounded-xl border border-emerald-500/20 bg-slate-900/70 p-4">
            <p className="text-sm font-semibold text-white">Selected Now</p>
            <p className="mt-2 text-2xl font-bold text-emerald-400">{selectedObjects.length}</p>
            <p className="mt-1 text-sm text-gray-400">active classes</p>
          </div>
        </div>

        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Object Selection</h2>
            <p className="text-sm text-gray-400 mt-1">
              Only checked classes are returned from the API.
            </p>
          </div>
          <button onClick={handleSave} className="btn-primary flex items-center justify-center gap-2">
            <Save className="w-4 h-4" />
            Save Settings
          </button>
        </div>

        <div className="settings-grid">
          {AVAILABLE_OBJECT_CLASSES.map(cls => {
            const checked = selectedObjects.includes(cls);
            return (
              <label key={cls} className={`settings-option ${checked ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleObject(cls)}
                  className="settings-checkbox"
                />
                <span className="flex items-center gap-3">
                  <CheckSquare className={`w-5 h-5 ${checked ? 'text-cyan-400' : 'text-slate-500'}`} />
                  <span className="text-white capitalize">{cls}</span>
                  {cls === 'person' && (
                    <span className="rounded-full bg-amber-500/15 px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-amber-300">
                      analytics
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>

        {error && (
          <div className="mt-6 rounded-lg border border-red-500 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}
        {savedMessage && (
          <div className="mt-6 rounded-lg border border-green-500 bg-green-500/10 px-4 py-3 text-sm text-green-300">
            {savedMessage}
          </div>
        )}
      </div>
    </div>
  );
}
